import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import { Modal, message } from "antd";
import { useAppDispatch, useAppSelector } from "../../store";
import {
  createCommentThunk,
  getCommentsThunk,
  getRepliesThunk,
  reactToPostThunk,
} from "../../store/actions/social.action";
import { useOptimisticUpdate } from "../../core/utils/useOptimisticUpdate";
import { timeAgo } from "../../core/utils/timeAgo";
import { CheckCircleFilled } from "@ant-design/icons";
import { MediaContent } from "./MediaContent";
import { ReactComponent as HeartIcon } from "../../assets/heart.svg";
import { ReactComponent as HeartOutlinedIcon } from "../../assets/heart_outlined.svg";
import Avatar from "boring-avatars";
import { formatNumber } from "../../core/utils/formatNumber";
import { useNavigate } from "react-router-dom";
import { ReactComponent as SendIcon } from "../../assets/send.svg";
import AuthGuard from "../AuthGuard";

interface CommentType {
  _id: string;
  content: string;
  createdAt: string;
  parentCommentId?: string;
  replies?: CommentType[];
  user: {
    _id: string;
    avatarUrl: string;
    firstName: string;
    lastName: string;
    email: string;
    paymentMode: number;
  };
  userReaction: string | undefined;
  replyCount: number;
  reactionSummary: {
    _id: string;
    count: number;
  }[];
}

interface PostType {
  _id: string;
  author: string;
  avatar: string;
  verified: boolean;
  title: string;
  content: string;
  videos: string[];
  images: string[];
  likes: number;
  userReaction: string;
  commentCount: number;
  createdAt: string;
  user: {
    _id: string;
    avatarUrl: string;
    firstName: string;
    lastName: string;
    email: string;
    paymentMode: number;
  };
  reactionSummary: {
    _id: string;
    count: number;
  }[];
}

interface CommentModalProps {
  isOpen: boolean;
  onClose: () => void;
  post: PostType;
}

export const CommentModal: React.FC<CommentModalProps> = ({
  isOpen,
  onClose,
  post,
}) => {
  const [newComment, setNewComment] = useState("");
  const [replyText, setReplyText] = useState("");
  const [replyingTo, setReplyingTo] = useState<CommentType | null>(null);
  const [commentPage, setCommentPage] = useState(1);
  const [replyPages, setReplyPages] = useState<{ [key: string]: number }>({});
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const replyTextareaRef = useRef<HTMLTextAreaElement>(null);
  const dispatch = useAppDispatch();
  const [
    comments,
    addOptimisticComment,
    removeOptimisticComment,
    updateComments,
  ] = useOptimisticUpdate<CommentType>([]);

  const navigate = useNavigate();
  const { me, userExtendData } = useAppSelector((state) => state.authReducer);
  const { comments: commentsData } = useAppSelector(
    (state) => state.socialReducer
  );

  const fetchComments = async (postId: string, page: number = 1) => {
    await dispatch(
      getCommentsThunk({
        postId: postId,
        page: page,
        limit: 5,
      })
    );
  };

  const fetchReplies = async (commentId: string, page: number = 1) => {
    const result = await dispatch(
      getRepliesThunk({ commentId, page, limit: 5 })
    ).unwrap();
    if (result) {
      updateComments(
        comments.map((comment) =>
          comment._id === commentId
            ? {
                ...comment,
                replies:
                  page === 1
                    ? result.data
                    : [...(comment.replies || []), ...result.data],
              }
            : comment
        )
      );
    }
  };

  const loadMoreComments = async () => {
    const nextPage = commentPage + 1;
    setCommentPage(nextPage);
    await fetchComments(post._id, nextPage);
  };

  const loadMoreReplies = async (commentId: string) => {
    const currentPage = replyPages[commentId] || 1;
    const nextPage = currentPage + 1;
    setReplyPages((prev) => ({ ...prev, [commentId]: nextPage }));
    await fetchReplies(commentId, nextPage);
  };

  const adjustTextareaHeight = (textarea: HTMLTextAreaElement | null) => {
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNewComment(e.target.value);
    adjustTextareaHeight(e.target);
  };

  const handleReplyTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setReplyText(e.target.value);
    adjustTextareaHeight(e.target);
  };

  const handleAddComment = async (content: string) => {
    // Create optimistic comment
    const optimisticComment: CommentType = {
      _id: "temp-" + Date.now(),
      content,
      createdAt: new Date().toISOString(),
      user: {
        _id: me?._id || me?.id || "",
        avatarUrl: userExtendData?.avatarUrl || "",
        firstName: me?.firstName || "",
        lastName: me?.lastName || "",
        email: me?.email || "",
        paymentMode: me?.paymentMode || 0,
      },
      replyCount: 0,
      userReaction: undefined,
      reactionSummary: [],
    };

    // Add optimistic comment
    addOptimisticComment(optimisticComment);

    try {
      // Make API call
      await dispatch(
        createCommentThunk({
          postId: post._id,
          content,
        })
      ).unwrap();

      // Refresh comments
      await fetchComments(post._id);
      setNewComment("");
    } catch (error) {
      // Remove optimistic comment on error
      removeOptimisticComment(optimisticComment._id);
      message.error("Failed to add comment");
    }
  };

  const handleAddReply = async (
    content: string,
    parentComment: CommentType
  ) => {
    // Create optimistic reply
    const optimisticReply: CommentType = {
      _id: "temp-" + Date.now(),
      content: content,
      createdAt: new Date().toISOString(),
      parentCommentId: parentComment._id,
      user: {
        _id: me?._id || me?.id || "",
        avatarUrl: userExtendData?.avatarUrl || "",
        firstName: me?.firstName || "",
        lastName: me?.lastName || "",
        email: me?.email || "",
        paymentMode: me?.paymentMode || 0,
      },
      replyCount: 0,
      userReaction: undefined,
      reactionSummary: [],
    };

    const newComments = comments.map((comment) => {
      if (comment._id === parentComment._id) {
        return {
          ...comment,
          replies: [optimisticReply, ...(comment.replies || [])],
          replyCount: (comment.replyCount || 0) + 1,
        };
      }
      return comment;
    });

    // Update comments with optimistic reply
    updateComments(newComments);

    try {
      // Make API call
      const result = await dispatch(
        createCommentThunk({
          postId: post._id,
          content: content,
          parentCommentId: parentComment._id,
        })
      ).unwrap();

      // Update the optimistic reply with the real data from the API
      if (result) {
        updateComments(
          newComments.map((comment) => {
            if (comment._id === parentComment._id) {
              // Keep existing replies and replace the optimistic one with the real one
              const existingReplies = comment.replies || [];
              const updatedReplies = existingReplies.map((reply) =>
                reply._id === optimisticReply._id
                  ? {
                      ...reply,
                      ...result,
                    }
                  : reply
              );

              return {
                ...comment,
                replies: updatedReplies,
              };
            }
            return comment;
          })
        );
      }

      setReplyText("");
      setReplyingTo(null);
    } catch (error) {
      // Remove optimistic reply on error
      updateComments(
        comments.map((comment) => {
          if (comment._id === parentComment._id) {
            return {
              ...comment,
              replies: (comment.replies || []).filter(
                (reply) => reply._id !== optimisticReply._id
              ),
              replyCount: (comment.replyCount || 0) - 1,
            };
          }
          return comment;
        })
      );
      message.error("Failed to add reply");
    }
  };

  const handleReply = (comment: CommentType) => {
    setReplyingTo(comment);
    setReplyText("");
    if (replyTextareaRef.current) {
      replyTextareaRef.current.focus();
    }
  };

  const reactToComment = async (commentId: string) => {
    // Find the comment or reply that's being liked
    const findAndUpdateReaction = (comments: CommentType[]): CommentType[] => {
      return comments.map((comment) => {
        // Check if this is the comment being liked
        if (comment._id === commentId) {
          const currentReaction = comment.userReaction;
          const currentLikeCount =
            comment.reactionSummary.find((r) => r._id === "like")?.count || 0;

          return {
            ...comment,
            userReaction: currentReaction ? undefined : "like",
            reactionSummary: [
              {
                _id: "like",
                count: currentReaction
                  ? currentLikeCount - 1
                  : currentLikeCount + 1,
              },
            ],
          };
        }

        // Check replies if they exist
        if (comment.replies) {
          return {
            ...comment,
            replies: comment.replies.map((reply) => {
              if (reply._id === commentId) {
                const currentReaction = reply.userReaction;
                const currentLikeCount =
                  reply.reactionSummary.find((r) => r._id === "like")?.count ||
                  0;

                return {
                  ...reply,
                  userReaction: currentReaction ? undefined : "like",
                  reactionSummary: [
                    {
                      _id: "like",
                      count: currentReaction
                        ? currentLikeCount - 1
                        : currentLikeCount + 1,
                    },
                  ],
                };
              }
              return reply;
            }),
          };
        }

        return comment;
      });
    };

    // Optimistically update the UI
    updateComments(findAndUpdateReaction(comments));

    try {
      await dispatch(
        reactToPostThunk({
          commentId: commentId,
        })
      ).unwrap();
    } catch (error) {
      // Revert the optimistic update on error
      updateComments(findAndUpdateReaction(comments));
      message.error("Failed to react to comment");
    }
  };

  useEffect(() => {
    if (commentPage === 1) {
      updateComments(commentsData.data);
    } else {
      // Merge new comments with existing ones
      updateComments([...comments, ...commentsData.data]);
    }
  }, [commentsData]);

  const ExpandableContent: React.FC<{ content: string }> = ({ content }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const maxLength = 200;

    if (content.length <= maxLength) {
      return <PostContent>{content}</PostContent>;
    }

    return (
      <PostContent>
        {isExpanded ? content : `${content.slice(0, maxLength)}...`}
        <SeeMoreButton onClick={() => setIsExpanded(!isExpanded)}>
          {isExpanded ? "See less" : "See more"}
        </SeeMoreButton>
      </PostContent>
    );
  };

  const renderComment = (comment: CommentType) => (
    <CommentItem key={comment._id}>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: 12,
          flex: 1,
        }}
      >
        <CommentHeader>
          {comment.user.avatarUrl ? (
            <img
              src={comment.user.avatarUrl}
              alt={`${comment.user.firstName} ${comment.user.lastName}`}
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                objectFit: "cover",
                cursor: "pointer",
              }}
              onClick={(event) => {
                event.stopPropagation();
                handleClose();
                navigate(`/user/${comment.user?._id}`);
              }}
            />
          ) : (
            <Avatar
              name={comment.user.firstName + " " + comment.user.lastName}
              variant="beam"
              style={{
                cursor: "pointer",
              }}
              size={40}
              onClick={(event) => {
                event.stopPropagation();
                handleClose();
                navigate(`/user/${comment.user?._id}`);
              }}
            />
          )}
        </CommentHeader>
        <CommentContent>
          <CommentInfo>
            <CommentAuthor
              onClick={(event) => {
                event.stopPropagation();
                handleClose();
                navigate(`/user/${comment.user?._id}`);
              }}
            >
              {comment.user.firstName} {comment.user.lastName}{" "}
              {comment.user.paymentMode === 4 && <Verified />}
            </CommentAuthor>
          </CommentInfo>
          {comment.content}
        </CommentContent>
        <AuthGuard>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
            }}
            onClick={() => {
              if (!me) return;
              reactToComment(comment._id);
            }}
          >
            {comment.userReaction ? (
              <HeartIcon style={{ width: 24, height: 24, cursor: "pointer" }} />
            ) : (
              <HeartOutlinedIcon
                style={{ width: 24, height: 24, cursor: "pointer" }}
              />
            )}
            <span style={{ fontSize: 12, color: "#fff", fontWeight: 600 }}>
              {formatNumber(
                comment.reactionSummary.find(
                  (reaction: any) => reaction._id === "like"
                )?.count || 0
              )}
            </span>
          </div>
        </AuthGuard>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 12,
          marginLeft: 60,
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: 12,
          }}
        >
          <CommentTime>{timeAgo(comment.createdAt)}</CommentTime>
          <AuthGuard>
            <ReplyButton
              onClick={() => {
                if (!me) return;
                handleReply(comment);
              }}
            >
              Reply
            </ReplyButton>
          </AuthGuard>
          {comment.replyCount > 0 && (
            <ViewReplies onClick={() => fetchReplies(comment._id)}>
              View replies
            </ViewReplies>
          )}
        </div>
        {comment.replies && comment.replies.length > 0 && (
          <RepliesContainer>
            {comment.replies.map((reply) => (
              <ReplyItem key={reply._id}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 12,
                    flex: 1,
                  }}
                >
                  <ReplyHeader>
                    {reply.user.avatarUrl ? (
                      <img
                        src={reply.user.avatarUrl}
                        alt={`${reply.user.firstName} ${reply.user.lastName}`}
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          objectFit: "cover",
                          cursor: "pointer",
                        }}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleClose();
                          navigate(`/user/${reply.user?._id}`);
                        }}
                      />
                    ) : (
                      <Avatar
                        name={reply.user.firstName + " " + reply.user.lastName}
                        variant="beam"
                        size={40}
                        style={{ cursor: "pointer" }}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleClose();
                          navigate(`/user/${reply.user?._id}`);
                        }}
                      />
                    )}
                  </ReplyHeader>
                  <ReplyContent>
                    <ReplyInfo>
                      <ReplyAuthor
                        onClick={(event) => {
                          event.stopPropagation();
                          handleClose();
                          navigate(`/user/${reply.user?._id}`);
                        }}
                      >
                        {reply.user.firstName} {reply.user.lastName}{" "}
                        {reply.user.paymentMode === 4 && <Verified />}
                      </ReplyAuthor>
                    </ReplyInfo>
                    {reply.content}
                  </ReplyContent>

                  <AuthGuard>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        flexDirection: "column",
                      }}
                      onClick={() => {
                        if (!me) return;
                        reactToComment(reply._id);
                      }}
                    >
                      {reply.userReaction ? (
                        <HeartIcon
                          style={{ width: 24, height: 24, cursor: "pointer" }}
                        />
                      ) : (
                        <HeartOutlinedIcon
                          style={{ width: 24, height: 24, cursor: "pointer" }}
                        />
                      )}
                      <span
                        style={{ fontSize: 12, color: "#fff", fontWeight: 600 }}
                      >
                        {formatNumber(
                          reply.reactionSummary.find(
                            (reaction: any) => reaction._id === "like"
                          )?.count || 0
                        )}
                      </span>
                    </div>
                  </AuthGuard>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                    marginLeft: 60,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      gap: 12,
                    }}
                  >
                    <ReplyTime>{timeAgo(reply.createdAt)}</ReplyTime>
                    <AuthGuard>
                      <ReplyButton
                        onClick={() => {
                          if (!me) return;
                          handleReply(reply);
                        }}
                      >
                        Reply
                      </ReplyButton>
                    </AuthGuard>
                  </div>
                </div>
              </ReplyItem>
            ))}
            {comment.replies.length < comment.replyCount && (
              <LoadMoreButton onClick={() => loadMoreReplies(comment._id)}>
                Load more replies
              </LoadMoreButton>
            )}
          </RepliesContainer>
        )}
        {(replyingTo?._id === comment._id ||
          comment._id === replyingTo?.parentCommentId) && (
          <ReplyInputContainer>
            <ReplyInput
              ref={replyTextareaRef}
              value={replyText}
              onChange={handleReplyTextChange}
              placeholder={`Reply to ${comment.user.firstName} ${comment.user.lastName} `}
              rows={1}
            />
            <ReplyInputActions>
              <ReplySubmitButton
                onClick={() => {
                  if (replyText.trim()) {
                    handleAddReply(replyText, comment);
                  }
                }}
                disabled={!replyText.trim()}
              >
                <SendIcon width={20} style={{ paddingLeft: 2 }} />
              </ReplySubmitButton>
            </ReplyInputActions>
          </ReplyInputContainer>
        )}
      </div>
    </CommentItem>
  );

  const handleClose = () => {
    onClose();
    setReplyingTo(null);
    setReplyText("");
    setNewComment("");
    setCommentPage(1);
    setReplyPages({});
    updateComments([]);
  };

  return (
    <Modal
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      width={
        post.images.length > 0 || post.videos.length > 0 ? "80svw" : "50svw"
      }
      centered
      wrapClassName="social-modal"
      style={{ padding: "20px 0" }}
      closeIcon={null}
    >
      <ModalContent>
        <ModalHeader>
          <h2>{post.user.firstName}'s Post</h2>
          <CloseButton onClick={handleClose}>×</CloseButton>
        </ModalHeader>
        <ModalBody>
          <CommentContainer>
            {(post.images.length > 0 || post.videos.length > 0) && (
              <MediaSection>
                <MediaContent
                  files={[
                    ...post.images.map((url) => ({ url, type: "image/jpeg" })),
                    ...post.videos.map((url) => ({ url, type: "video/mp4" })),
                  ]}
                />
              </MediaSection>
            )}
            <ContentSection>
              <ContentContainer>
                <PostSection>
                  <PostCardWrapper>
                    <PostHeader>
                      {post.user.avatarUrl ? (
                        <img
                          style={{
                            width: 52,
                            height: 52,
                            borderRadius: "50%",
                            objectFit: "cover",
                            cursor: "pointer",
                          }}
                          src={post.user.avatarUrl}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleClose();
                            navigate(`/user/${post.user?._id}`);
                          }}
                        />
                      ) : (
                        <Avatar
                          name={post.user.firstName + " " + post.user.lastName}
                          variant="beam"
                          size={52}
                          style={{
                            cursor: "pointer",
                          }}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleClose();
                            navigate(`/user/${post.user?._id}`);
                          }}
                        />
                      )}
                      <PostUser>
                        <UsernameRow>
                          <Username
                            onClick={(event) => {
                              event.stopPropagation();
                              handleClose();
                              navigate(`/user/${post.user?._id}`);
                            }}
                          >
                            {post.user.firstName} {post.user.lastName}
                          </Username>
                          {post.user.paymentMode === 4 && <Verified />}
                        </UsernameRow>
                        <PostTime>{timeAgo(post.createdAt)}</PostTime>
                      </PostUser>
                    </PostHeader>
                    <ExpandableContent content={post.content} />
                  </PostCardWrapper>
                </PostSection>

                <CommentSection>
                  <CommentList>
                    {comments.map((comment) => renderComment(comment))}
                    {comments.length === 0 && (
                      <CommentEmpty>No comments yet</CommentEmpty>
                    )}
                    {comments.length < commentsData.pagination.totalItems && (
                      <LoadMoreButton onClick={loadMoreComments}>
                        Load more comments
                      </LoadMoreButton>
                    )}
                  </CommentList>
                </CommentSection>
              </ContentContainer>

              <CommentInputContainer>
                <AuthGuard>
                  <CommentInput
                    ref={textareaRef}
                    value={newComment}
                    onChange={handleCommentChange}
                    placeholder="Write a comment..."
                    rows={1}
                    readOnly={!me}
                  />
                </AuthGuard>
                <AuthGuard>
                  <CommentButton
                    onClick={() => {
                      if (newComment.trim()) {
                        handleAddComment(newComment);
                      }
                    }}
                    disabled={!newComment.trim()}
                  >
                    <SendIcon width={20} style={{ paddingLeft: 2 }} />
                  </CommentButton>
                </AuthGuard>
              </CommentInputContainer>
            </ContentSection>
          </CommentContainer>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const ModalContent = styled.div`
  background: #282828;
  border-radius: 24px;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #333;
  display: flex;
  justify-content: space-between;
  align-items: center;

  h2 {
    color: #fff;
    margin: 0;
    font-size: 1.5rem;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #bdbdbd;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  line-height: 1;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const ModalBody = styled.div`
  gap: 0.5rem;
  display: flex;
  flex-direction: column;
  height: 80svh;
`;

const CommentContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
  padding: 1.5rem;
  height: 100%;
  box-sizing: border-box;
`;

const MediaSection = styled.div`
  flex: 1;
  background: #222;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const ContentSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PostSection = styled.div`
  border-bottom: 1px solid #333;
  padding-bottom: 12px;
`;

const CommentSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  min-height: 0;
  gap: 12px;
`;

const PostCardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const PostHeader = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const PostUser = styled.div`
  display: flex;
  flex-direction: column;
  margin: auto 0;
`;

const UsernameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const Username = styled.span`
  color: #fff;
  font-weight: 600;
  font-size: 1rem;

  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const Verified = styled(CheckCircleFilled)`
  color: #ff8e53;
  font-size: 1rem;
`;

const PostTime = styled.span`
  color: #bdbdbd;
  font-size: 0.9rem;
`;

const PostContent = styled.div`
  color: #d1d5db;
  font-size: 1rem;
  line-height: 1.5;
  white-space: pre-wrap;
`;

const SeeMoreButton = styled.button`
  background: none;
  border: none;
  color: #ff8e53;
  padding: 0;
  margin-left: 4px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
  flex: 1;
  padding-right: 12px;

  /* Hide default scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #1a1924; /* Track color */
    border: 1px solid #242424;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: #333333; /* Thumb color */
    border-radius: 10px; /* Rounded edges */
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #363636; /* Color on hover */
  }

  /* Optional: Add a custom arrow */
  &::-webkit-scrollbar-corner {
    background: transparent; /* Corner color */
  }
`;

const CommentList = styled.div`
  flex: 1;
  gap: 12px;
  display: flex;
  flex-direction: column;
`;

const CommentItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CommentHeader = styled.div`
  display: flex;
`;

const CommentInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const CommentAuthor = styled.span`
  color: #fff;
  font-weight: 600;
  font-size: 0.95rem;

  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const CommentTime = styled.span`
  color: #bdbdbd;
  font-size: 0.7rem;
`;

const CommentContent = styled.div`
  color: #d1d5db;
  font-size: 0.95rem;
  background: #222;
  flex: 1;
  padding: 6px 12px;
  border-radius: 8px;
`;

const CommentInputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: #222;
  border-radius: 12px;
  z-index: 1;
`;

const CommentInput = styled.textarea`
  flex: 1;
  background: none;
  border: none;
  color: #fff;
  font-size: 0.95rem;
  outline: none;
  font-family: "Inter", sans-serif;
  resize: none;
  min-height: 30px;
  max-height: 120px;
  overflow-y: auto;
  box-sizing: border-box;

  /* Hide default scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #1a1924; /* Track color */
    border: 1px solid #242424;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: #333333; /* Thumb color */
    border-radius: 10px; /* Rounded edges */
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #363636; /* Color on hover */
  }

  /* Optional: Add a custom arrow */
  &::-webkit-scrollbar-corner {
    background: transparent; /* Corner color */
  }

  &::placeholder {
    color: #666;
  }
`;

const CommentButton = styled.button`
  background: #ff6b6b;
  border: none;
  color: #fff;
  border-radius: 20px;
  cursor: pointer;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:disabled {
    background: #666;
    cursor: not-allowed;
  }
`;

const CommentEmpty = styled.div`
  color: #686868;
  font-size: 0.95rem;
  text-align: center;
  margin-top: 12px;
`;

const ReplyButton = styled.p`
  color: #ff8e53;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 500;
  margin: 0;

  &:hover {
    text-decoration: underline;
  }
`;

const ViewReplies = styled.p`
  font-size: 12px;
  font-weight: 500;
  margin: 0;
  color: #d1d5db;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const RepliesContainer = styled.div`
  border-left: 2px solid #333;
  padding-left: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ReplyItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ReplyHeader = styled.div`
  display: flex;
`;

const ReplyInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const ReplyAuthor = styled.span`
  color: #fff;
  font-weight: 600;
  font-size: 0.95rem;

  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const ReplyTime = styled.span`
  color: #bdbdbd;
  font-size: 0.7rem;
`;

const ReplyContent = styled.div`
  color: #d1d5db;
  font-size: 0.95rem;
  background: #222;
  flex: 1;
  padding: 4px 10px;
  border-radius: 8px;
`;

const ReplyInputContainer = styled.div`
  margin: 0 36px 0 0;
  background: #222;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  flex-direction: row;
  gap: 12px;
`;

const ReplyInput = styled.textarea`
  width: 100%;
  background: none;
  border: none;
  color: #fff;
  font-size: 0.95rem;
  outline: none;
  font-family: "Inter", sans-serif;
  resize: none;
  min-height: 30px;
  max-height: 120px;
  overflow-y: auto;
  box-sizing: border-box;

  &::placeholder {
    color: #666;
  }

  /* Hide default scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #1a1924;
    border: 1px solid #242424;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: #333333;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #363636;
  }
`;

const ReplyInputActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const ReplySubmitButton = styled.button`
  background: #ff6b6b;
  border: none;
  color: #fff;
  border-radius: 20px;
  cursor: pointer;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:disabled {
    background: #666;
    cursor: not-allowed;
  }
`;

const LoadMoreButton = styled.button`
  background: none;
  border: none;
  color: #ff8e53;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  width: 100%;
  text-align: center;

  &:hover {
    text-decoration: underline;
  }
`;
